import {
    GatewayByIdResponse,
} from 'app/requests/Gateway/Gateway.types'
import {
    DTOCardFormField,
} from 'app/types/form.types'
import {
    LocationType,
    GatewayDataPlan,
    GatewayLocationCategory,
    GatewayStatus,
    GatewayStorageArea,
    GatewayType,
    GatewayMainType,
    GatewayHardwareVersion,
    GatewaySoftwareVersion,
} from 'app/enums/label.enums'

const getFields = ({
    gatewayStatus,
    showConnectedLogger = true,
    showConnectionInfo = true,
    getAllowedValues,
    locationId = undefined,
    disabled = false,
}: {
    showConnectedLogger: boolean,
    showConnectionInfo: boolean,
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    locationId: number,
    disabled: boolean,
    gatewayStatus?: GatewayStatus,
}): DTOCardFormField<keyof GatewayByIdResponse>[] => {
    return [
        {
            id: 'gateway',
            title: 'Gateway',
            className: 'gateway',
            fields: [
                {
                    id: 'column1',
                    fields: [
                        {
                            name: 'gatewayImeiMac',
                            labelKey: 'Gateway.gatewayImeiMac',
                            componentName: 'InputSingleline',
                        },
                        {
                            name: 'gatewaySimCardNumber',
                            labelKey: 'Gateway.gatewaySimCardNumber',
                            componentName: 'InputSingleline',
                        },
                        {
                            name: 'gatewaySimCardNumberBackup',
                            labelKey: 'GatewayCreateDto.gatewaySimCardNumberBackup',
                            componentName: 'InputSingleline',
                        },
                        {
                            name: 'hardwareVersion',
                            labelKey: 'Gateway.hardwareVersion',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayHardwareVersion),
                            useDropdown: true,
                            required: gatewayStatus !== GatewayStatus.DECOMMISSIONED,
                        },
                        {
                            name: 'softwareVersion',
                            labelKey: 'Gateway.softwareVersion',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewaySoftwareVersion),
                            useDropdown: true,
                            required: gatewayStatus !== GatewayStatus.DECOMMISSIONED,
                        },
                        {
                            name: 'installedOn',
                            labelKey: 'Gateway.installedOn',
                            componentName: 'DateTimeSelect',
                            required: gatewayStatus === GatewayStatus.INSTALLED,
                        },
                        {
                            name: 'installedBy',
                            componentName: 'ContactObjectSelector',
                            labelKey: 'Gateway.installedBy',
                        },
                        {
                            name: 'supportingDocuments',
                            labelKey: 'Accounts.supportingDocuments',
                            componentName: 'AttachmentsObjectSelector',
                        },
                    ],
                },
                {
                    id: 'column2',
                    fields: [
                        {
                            name: 'gatewayMainType',
                            labelKey: 'Gateway.gatewayMainType',
                            className: 'gatewayType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayMainType),
                            useDropdown: true,
                            disabled: true,
                        },
                        {
                            name: 'gatewayType',
                            labelKey: 'Gateway.gatewayType',
                            className: 'gatewayType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayType),
                            useDropdown: true,
                        },
                        {
                            name: 'gatewayConnectionType',
                            labelKey: 'Gateway.gatewayConnectionType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayDataPlan),
                            useDropdown: true,
                            required: true,
                        },
                        {
                            name: 'gatewayStatus',
                            labelKey: 'Gateway.gatewayStatus',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayStatus),
                            useDropdown: true,
                            required: true,
                        },
                        {
                            name: 'gatewayDeployment',
                            labelKey: 'Deployment.deploymentName',
                            componentName: 'DeploymentSelector',
                            required: true,
                        },
                        {
                            name: 'remark',
                            labelKey: 'Gateway.remark',
                            className: 'remark',
                            componentName: 'LongText',
                        },
                    ],
                },
            ],
        },
        {
            id: 'location',
            title: 'Location',
            className: 'location',
            fields: [
                {
                    id: 'column1',
                    fields: [
                        {
                            name: 'location',
                            labelKey: 'Gateway.location',
                            componentName: 'LocationObjectDataSelector',
                            filterLocationType: LocationType.ADDRESS,
                            isShippingLocation: true,
                            required: true,
                        },
                        {
                            name: 'area',
                            labelKey: 'Gateway.area',
                            required: gatewayStatus === GatewayStatus.TO_BE_INSTALLED
                                || gatewayStatus === GatewayStatus.INSTALLED,
                            componentName: 'AreaSelector',
                            locationId,
                        },
                        {
                            name: 'locationRemark',
                            labelKey: 'Gateway.locationRemark',
                            componentName: 'InputSingleline',
                        },
                        {
                            name: 'gatewayStorageArea',
                            labelKey: 'Gateway.gatewayStorageArea',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayStorageArea),
                        },
                    ],
                },
                {
                    id: 'column2',
                    fields: [
                        {
                            name: 'latitude',
                            componentName: 'InputSingleline',
                            labelKey: 'Gateway.latitude',
                            required: true,
                        },
                        {
                            name: 'longitude',
                            componentName: 'InputSingleline',
                            labelKey: 'Gateway.longitude',
                            required: true,
                        },
                        {
                            name: 'gatewayLocationCategories',
                            labelKey: 'GatewayView.gatewayLocationCategories',
                            componentName: 'MultiEnumSelector',
                            disabled: true,
                            allowedValues: getAllowedValues(GatewayLocationCategory),
                        },
                    ],
                },
            ],
        },
        {
            title: 'Connection Information',
            className: 'connectionInfo',
            id: 'connectionInfo',
            hidden: !showConnectionInfo,
            fields: [{
                name: 'lastConnectedOn',
                disabled: true,
                labelKey: 'Gateway.lastConnectedOn',
                componentName: 'TemperatureAge',
            }],
        },
        {
            title: 'Gateway Contacts',
            className: 'contacts',
            id: 'contacts',
            fields: [disabled ? {
                name: 'gatewayContacts',
                componentName: 'GatewayContacts',
            } : {
                name: 'gatewayContacts',
                componentName: 'MultipleContactObjectSelector',
                required: gatewayStatus === GatewayStatus.TO_BE_INSTALLED
                    || gatewayStatus === GatewayStatus.INSTALLED,
                value: [],
            }],
        },
        {
            title: 'Connected Loggers',
            className: 'loggers',
            id: 'loggers',
            hidden: !showConnectedLogger,
            fields: [{
                name: 'id',
                componentName: 'ConnectedLoggers',
            }],
        },
    ]
}

export default getFields
